import { useEffect, useState, useCallback } from "react";

/** Hook to expose a `beforeinstallprompt` based PWA install action.
 *
 * Returns:
 *  - canInstall: boolean — whether the browser fired beforeinstallprompt
 *  - isStandalone: boolean — already running as installed PWA
 *  - promptInstall: () => void — call to show the native prompt
 */
export function usePWAInstall() {
  const [evt, setEvt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setEvt(e);
    };
    const onInstalled = () => {
      setEvt(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!evt) return false;
    evt.prompt();
    const choice = await evt.userChoice;
    setEvt(null);
    return choice?.outcome === "accepted";
  }, [evt]);

  return { canInstall: !!evt, isStandalone, promptInstall };
}
